import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, ChartLineUp, FilePlus, MagnifyingGlass, Strategy, Coins, ShareNetwork, Quotes, Scroll, Trophy, Certificate, Handshake } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { MaskedLines, MaskedLinesOnScroll, Reveal, spotlightHandler } from "@/components/motion/Reveal";
import ParallaxFrame from "@/components/site/ParallaxFrame";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { WhatsappLogo, Phone } from "@phosphor-icons/react";
import IpTrainingEnrollForm from "@/components/site/IpTrainingEnrollForm";
import { useSeo } from "@/lib/useSeo";
import { trackWhatsAppClick, trackCallClick } from "@/lib/track";

const WA_LINK =
  "https://wa.me/919819998036?text=" +
  encodeURIComponent("Hi, I'd like to know more about the IP training programmes.");

const COURSES = [
  { slug: "patent-analytics", title: "Patent Analytics", tagline: "Decode landscapes with data.", icon: ChartLineUp, duration: "1:1 · 45–60 min" },
  { slug: "patent-drafting", title: "Patent Drafting", tagline: "Write claims that hold up.", icon: FilePlus, duration: "1:1 · 45–60 min" },
  { slug: "trademark-search", title: "Trademark Search", tagline: "Clear marks before they cost.", icon: MagnifyingGlass, duration: "1:1 · 45–60 min" },
  { slug: "ip-strategy", title: "IP Strategy", tagline: "Turn IP into leverage.", icon: Strategy, duration: "1:1 · 45–60 min" },
  { slug: "patent-valuation", title: "Patent Valuation", tagline: "Put a defensible number on it.", icon: Coins, duration: "1:1 · 45–60 min" },
  { slug: "technology-transfer", title: "Technology Transfer", tagline: "Lab to market, deal to ink.", icon: ShareNetwork, duration: "1:1 · 45–60 min" },
  { slug: "introduction-to-ip-law", title: "Introduction to IP Law and Indian Patent Law", tagline: "Your first window into Indian IP law.", icon: Scroll, duration: "1:1 · 45–60 min" },
  { slug: "indian-trademark-agent-exam", title: "How to ace the Indian Trademark Agent Exam", tagline: "Exam-focused, scenario-driven prep.", icon: Trophy, duration: "1:1 · 45–60 min" },
  { slug: "certified-patent-valuation-analyst", title: "Guide to become Certified Patent Valuation Analyst (CPVA)", tagline: "Your roadmap to the CPVA credential.", icon: Certificate, duration: "1:1 · 45–60 min" },
  { slug: "ip-commercialisation", title: "Guide to IP Commercialisation", tagline: "Turn patents into revenue.", icon: Handshake, duration: "1:1 · 45–60 min" },
];

const FAQS = [
  {
    q: "Who teaches the courses?",
    a: "Every programme is taught personally by Venkataramanan Mandakolathur — a Certified Patent Valuation Analyst, named inventor on 50+ granted patents, with 18+ years of global IP experience. You learn directly from a practitioner, not a recorded lecturer.",
  },
  {
    q: "Do I need a science or engineering background?",
    a: "For drafting and analytics tracks, a technical background helps but isn't mandatory. Strategy, valuation and IP-law foundation courses are open to anyone — lawyers, founders and business professionals included. If you're unsure, we'll match you to the right starting point.",
  },
  {
    q: "Are sessions live or recorded?",
    a: "All sessions are live and 1:1 over Zoom, so you can ask questions and work through real cases in real time. This is deliberately not a pre-recorded course library.",
  },
  {
    q: "Is there a certificate?",
    a: "Yes — you receive a certificate of completion for each programme you finish.",
  },
  {
    q: "How do I pay, and what does it cost?",
    a: "Fees vary by programme. Once we understand your background and goals, we'll recommend the right track and share the fee. Request enrolment and we'll get back within one business day.",
  },
  {
    q: "How long is each programme?",
    a: "Sessions run 45–60 minutes each, scheduled across a cohort to suit working professionals. Total length depends on the track — we'll walk you through the structure before you commit.",
  },
  {
    q: "Can I take more than one programme?",
    a: "Yes. Many learners combine tracks — for example, Patent Drafting with Patent Analytics, or IP Strategy with Valuation. We'll suggest a sequence based on your goals.",
  },
  {
    q: "Will this help me clear the Indian Trademark Agent Exam or the CPVA?",
    a: "Yes — there are dedicated, exam-focused programmes for both, built around the actual exam structure and scenarios.",
  },
];

const BEATS = [
  {
    title: "1:1, never one-to-many.",
    body: "Every learner is paired with a mentor for the whole cohort.",
  },
  {
    title:
      "Your work is reviewed line by line — the way a senior partner reviews a junior's drafting.",
    body: "Real cases, not textbooks. You learn from two or three real patent case studies per topic — what actually happens at the USPTO, EPO and IPO India, not theory.",
  },
  {
    title: "Built for working professionals.",
    body: "Live weekday and weekend Zoom sessions that fit around a job. Designed for engineers, attorneys, R&D managers and students alike.",
  },
  {
    title: "Taught by a practitioner.",
    body: "Not a full-time trainer — a working Certified Patent Valuation Analyst with 50+ granted patents. You learn the craft from someone who still does the work.",
  },
];

const TESTIMONIALS = [
  {
    quote: (
      <>
        I had the privilege to attend the following enriching sessions associated with
        Patent Analytics area:
        <ol className="mt-3 mb-3 list-decimal pl-5 space-y-1 text-base font-light text-[#B9C4D0]">
          <li>Beginners Guide to AI Patent Analytics and Patent Search</li>
          <li>AI based Patentability Search</li>
          <li>AI based Patent Landscape Analysis</li>
          <li>AI based Invalidity Search</li>
          <li>AI based Freedom To Operate (FTO) Search</li>
        </ol>
        I found all the above sessions to be very informative and excellent start for
        beginners. I thank Venkataramanan Mandakolathur from Gurukul of Ramana for patiently
        explaining the core concepts in all these sessions in very organised, time bound and
        easy to understand format.
        <span className="block mt-3">
          I recommend the above sessions to any students, IP professionals, etc who wants to
          learn job oriented sessions on Patent Analytics area and upgrade their skills.
        </span>
      </>
    ),
    name: "Dr. Priya Kush",
    role: "Patent Associate, Lexorbis",
    img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_ramana-learning/artifacts/mvympywr_IMG_1664.jpeg",
  },
  {
    quote: "I recently completed two insightful courses: AI-Based Patentability Search and Patent Invalidity Search. Both courses were well-structured, easy to follow, and provided practical insights that helped me strengthen and refresh my patent searching skills. The sessions effectively combined fundamental concepts with modern AI-driven approaches used in patent research. I would highly recommend these courses to both freshers entering the IP field and experienced patent professionals looking to enhance their search capabilities. A big thank you to Venkataramanan Mandakolathur and Gurukul of Ramana for delivering such valuable learning opportunities.",
    name: "Aparna Gautam",
    role: "Patent Analyst, UnitedLex",
    img: "https://customer-assets.emergentagent.com/job_ramana-learning/artifacts/4bwb1fwu_IMG_1298.jpeg",
  },
  {
    quote: "It was a truly enriching experience learning about Patent Landscape Analysis in today's session. The topic was explained exceptionally well, supported by two insightful live case studies, and Venkataramanan sir addressed all queries with great clarity and depth.",
    name: "Dr. Arpita Banerjee",
    role: "Aspiring Patent Professional",
    img: "https://customer-assets.emergentagent.com/job_ramana-learning/artifacts/gu04s5dq_IMG_1299.jpeg",
  },
  {
    quote: "A must for working professionals. Venkataramanan has solid fundamentals and expertise in Intellectual Property. Looking forward to completion and more. Need of the hour on AI Patent Analytics!",
    name: "Kumar Subramanyam",
    role: "Marketing Director, Chennai",
    img: "https://customer-assets.emergentagent.com/job_ramana-learning/artifacts/n2o4vixw_IMG_1301.jpeg",
  },
  {
    quote: "I recently completed the 'Beginner's Guide to AI in Patent Search and Patent Analytics' course, and it was an excellent learning experience. The course provides a clear and structured introduction to how AI can be effectively applied in patent searching and analytics. Overall, this course is a great starting point for anyone looking to understand the role of AI in intellectual property and improve efficiency in patent analysis. Highly recommended!",
    name: "Nikhil Dighe",
    role: "Patent Researcher, Johnson & Johnson Ltd",
    img: "https://customer-assets.emergentagent.com/job_ramana-learning/artifacts/y1twftf4_IMG_1302.jpeg",
  },
  {
    quote: "Excellent course on developing Intellectual Property Strategy. The concepts were explained clearly and in a simple manner, making it easy for beginners to understand the importance of IP Strategy. A valuable learning experience indeed!",
    name: "Ravishanker Vasudevan",
    role: "Health Safety Officer, GEMS Education, Dubai",
    img: "https://customer-assets.emergentagent.com/job_ramana-learning/artifacts/t45aamdj_IMG_1300.jpeg",
  },
];

export default function IpTrainingPage() {
  useSeo(
    "IP Training Courses Online — Patent Drafting, Analytics & Valuation | Gurukul of Ramana",
    "Live 1:1 online IP training by a CPVA with 50+ patents — patent drafting, analytics, valuation, strategy, and Trademark Agent & CPVA exam prep. Request enrolment.",
  );
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancel = false;
    api
      .get("/blog")
      .then((r) => { if (!cancel) setPosts((r.data || []).slice(0, 3)); })
      .catch(() => { if (!cancel) setPosts([]); });
    return () => { cancel = true; };
  }, []);

  return (
    <div data-testid="ip-training-page" className="bg-navy">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-[#C6A87C]/15"
      >
        <div className="absolute inset-0 blueprint-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute inset-0 noise-overlay opacity-10 pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 pt-44 pb-24 lg:pt-56 lg:pb-32 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal y={14}>
              <div className="label-overline flex items-center gap-3">
                <span className="h-px w-10 bg-[#C6A87C]" />
                Est. — A modern guru-shishya school
              </div>
            </Reveal>
            <MaskedLines
              testid="hero-title"
              delay={0.15}
              className="mt-6 text-4xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-[#F5F4F0] font-medium"
              lines={[
                <>Learn IP from someone</>,
                <>
                  who <em className="not-italic text-[#C6A87C]">still practises it.</em>
                </>,
              ]}
            />
            <motion.p
              data-testid="hero-subtitle"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.9 }}
              className="mt-8 text-lg md:text-xl font-light text-[#8A99AA] leading-relaxed max-w-2xl"
            >
              Ten 1:1 online programmes — patent drafting, analytics, valuation, strategy
              and more — mentored personally by a Certified Patent Valuation Analyst with
              50+ granted patents and 18+ years in global IP.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-6"
            >
              <Link
                to="#enroll"
                data-testid="hero-cta-enroll"
                className="group relative inline-flex items-center gap-3 h-14 px-8 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.14em] uppercase overflow-hidden"
              >
                <span className="absolute inset-0 bg-[#14B981] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
                <span className="relative">Request enrolment</span>
                <ArrowRight size={15} weight="bold" className="relative transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="#courses"
                data-testid="hero-cta-explore"
                className="group inline-flex items-center gap-2 py-4 text-xs tracking-[0.14em] uppercase text-[#F5F4F0]/75 hover:text-[#C6A87C] transition-colors duration-300"
              >
                Explore programmes
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.15, duration: 1 }}
              className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
            >
              {[
                ["1:1", "Personal mentorship"],
                ["10", "Practitioner-led programmes"],
                ["100%", "Live, via Zoom"],
                ["1000+", "Learners mentored"],
              ].map(([n, l]) => (
                <div key={l} className="border-l border-[#C6A87C]/40 pl-4">
                  <div className="font-serif text-3xl text-[#F5F4F0]">{n}</div>
                  <div className="label-overline mt-1 !text-[10px] text-[#8A99AA]">{l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.3}>
              <ParallaxFrame
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_ramana-learning/artifacts/vqfvussh_WhatsApp%20Image%202026-09-12%20at%2010.07.21.jpeg"
                alt="Venkataramanan Mandakolathur — Certified Patent Valuation Analyst and IP mentor"
                className="aspect-[4/5]"
                caption="Venkataramanan Mandakolathur — CPVA, 50+ granted patents, 18+ years in global IP."
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* COURSES */}
      <section id="courses" data-testid="courses-section" className="relative bg-navy py-24 lg:py-32 border-b border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <div>
              <Reveal>
                <div className="label-overline flex items-center gap-3">
                  <span className="h-px w-10 bg-[#C6A87C]" />
                  The Curriculum
                </div>
              </Reveal>
              <MaskedLinesOnScroll
                className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[#F5F4F0] max-w-3xl"
                lines={[
                  <>Ten programmes covering</>,
                  <>
                    the <em className="italic text-[#C6A87C]">full IP value chain.</em>
                  </>,
                ]}
              />
            </div>
            <Reveal delay={0.2}>
              <Link to="/courses" data-testid="courses-section-view-all" className="group inline-flex items-center gap-2 text-[#F5F4F0]/75 hover:text-[#C6A87C] text-xs tracking-[0.14em] uppercase transition-colors duration-300">
                View all programmes
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {COURSES.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.slug} delay={Math.min(i * 0.06, 0.4)} y={24}>
                  <Link
                    to={`/courses/${c.slug}`}
                    data-testid={`course-card-${c.slug}`}
                    onMouseMove={spotlightHandler}
                    className="spotlight-card group block h-full bg-navy-deep border border-[#C6A87C]/15 p-8 transition-colors duration-500 hover:border-[#C6A87C]/50"
                  >
                    <div className="flex items-start justify-between">
                      <span className="w-12 h-12 border border-[#C6A87C]/30 text-[#C6A87C] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#C6A87C] group-hover:text-[#061324]">
                        <Icon size={22} weight="light" />
                      </span>
                      <span className="label-overline !text-[10px]">{c.duration}</span>
                    </div>
                    <h3 className="mt-6 font-serif text-2xl text-[#F5F4F0] group-hover:text-[#C6A87C] transition-colors duration-300 leading-tight">
                      {c.title}
                    </h3>
                    <p className="mt-2 text-[#8A99AA] font-light text-sm leading-relaxed">{c.tagline}</p>
                    <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#C6A87C]">
                      Open syllabus
                      <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US — bento */}
      <section data-testid="why-us-section" className="bg-abyss py-24 lg:py-32 border-b border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24">
          <div className="max-w-3xl">
            <Reveal>
              <div className="label-overline flex items-center gap-3">
                <span className="h-px w-10 bg-[#C6A87C]" />
                Why Gurukul of Ramana
              </div>
            </Reveal>
            <MaskedLinesOnScroll
              className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[#F5F4F0]"
              lines={[
                <>Rigour over recordings.</>,
                <>
                  Practice <em className="italic text-[#C6A87C]">over slides.</em>
                </>,
              ]}
            />
          </div>

          <div className="mt-16 lg:mt-24 divide-y divide-[#C6A87C]/12 border-t border-[#C6A87C]/12">
            {BEATS.map((beat, i) => (
              <Reveal key={i} y={40} testid={`beat-${i}`}>
                <div className="py-14 lg:py-24 grid lg:grid-cols-12 gap-6 lg:gap-12 items-start">
                  <div className="lg:col-span-2">
                    <span className="font-mono-tech text-sm uppercase tracking-[0.24em] text-[#C6A87C]">
                      {String(i + 1).padStart(2, "0")} / 04
                    </span>
                  </div>
                  <div className="lg:col-span-10">
                    <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-[#F5F4F0] max-w-4xl">
                      {beat.title}
                    </h3>
                    <p className="mt-6 text-lg lg:text-xl text-[#8A99AA] font-light leading-relaxed max-w-3xl">
                      {beat.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" data-testid="testimonials-section" className="py-24 lg:py-32 bg-navy border-b border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24">
          <Reveal>
            <div className="label-overline flex items-center gap-3">
              <span className="h-px w-10 bg-[#C6A87C]" />
              Voices from the Cohort
            </div>
          </Reveal>
          <MaskedLinesOnScroll
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[#F5F4F0] max-w-2xl"
            lines={[<>What our</>, <><em className="italic text-[#C6A87C]">learners say.</em></>]}
          />

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={Math.min(i * 0.08, 0.4)} testid={`testimonial-${i}`}>
                <figure
                  onMouseMove={spotlightHandler}
                  className="spotlight-card h-full bg-navy-deep border border-[#C6A87C]/15 p-8 flex flex-col"
                >
                  <Quotes size={28} weight="fill" className="text-[#C6A87C]" />
                  <blockquote className="mt-6 font-serif text-xl text-[#F5F4F0] leading-snug">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-auto pt-8 flex items-center gap-3">
                    {t.img ? (
                      <img src={t.img} alt="" className="w-11 h-11 object-cover grayscale rounded-full border border-[#C6A87C]/30" />
                    ) : (
                      <div className="w-11 h-11 rounded-full border border-[#C6A87C]/30 bg-navy flex items-center justify-center text-[#C6A87C] text-sm font-semibold">
                        {t.name.replace(/^Dr\.?\s+/i, "").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-semibold text-[#F5F4F0]">{t.name}</div>
                      <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A99AA] mt-0.5">{t.role}</div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-24 lg:py-32 bg-abyss border-b border-[#C6A87C]/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="label-overline flex items-center gap-3">
              <span className="h-px w-10 bg-[#C6A87C]" />
              Frequently asked
            </div>
          </Reveal>
          <MaskedLinesOnScroll
            className="mt-4 text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#F5F4F0]"
            lines={[<>Before you <em className="italic text-[#C6A87C]">enrol.</em></>]}
          />

          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-12">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-b border-[#C6A87C]/15 last:border-b-0">
                  <AccordionTrigger className="font-serif text-xl lg:text-2xl text-[#F5F4F0] text-left hover:no-underline hover:text-[#C6A87C] transition-colors duration-300 py-7">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#8A99AA] font-light leading-relaxed pb-7">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ENROLMENT */}
      <section
        id="enroll"
        data-testid="final-cta-section"
        className="bg-navy text-white relative overflow-hidden border-b border-[#C6A87C]/10 scroll-mt-28"
      >
        <div className="absolute inset-0 blueprint-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute inset-0 noise-overlay opacity-[0.1] pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="label-overline flex items-center gap-3">
                <span className="h-px w-10 bg-[#C6A87C]" />
                Open enrolment
              </div>
            </Reveal>
            <MaskedLinesOnScroll
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-[#F5F4F0]"
              lines={[
                <>Request your</>,
                <>
                  <em className="italic text-[#C6A87C]">enrolment.</em>
                </>,
              ]}
            />
            <Reveal delay={0.2}>
              <p className="mt-7 text-[#8A99AA] font-light max-w-md text-lg leading-relaxed">
                Tell us about your background and the programme you have in mind. We&rsquo;ll
                match you with the right track and share the fee within one business day.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 pt-8 border-t border-[#C6A87C]/15">
                <div className="label-overline mb-4 text-[#8A99AA]">Prefer to talk first?</div>
                <div className="flex flex-wrap gap-4">
                  <a
                    href={WA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="enroll-whatsapp"
                    onClick={() => trackWhatsAppClick("ip_training_enroll")}
                    className="inline-flex items-center gap-2 h-11 px-5 border border-[#C6A87C]/30 text-[#F5F4F0]/85 text-xs tracking-[0.14em] uppercase hover:border-[#C6A87C] hover:text-[#C6A87C] transition-colors duration-300"
                  >
                    <WhatsappLogo size={16} weight="fill" />
                    WhatsApp
                  </a>
                  <a
                    href="tel:+919819998036"
                    data-testid="enroll-call"
                    onClick={() => trackCallClick("ip_training_enroll")}
                    className="inline-flex items-center gap-2 h-11 px-5 border border-[#C6A87C]/30 text-[#F5F4F0]/85 text-xs tracking-[0.14em] uppercase hover:border-[#C6A87C] hover:text-[#C6A87C] transition-colors duration-300"
                  >
                    <Phone size={16} weight="fill" />
                    Call us
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <IpTrainingEnrollForm />
          </div>
        </div>
      </section>

      {/* BLOG / INSIGHTS */}
      <section data-testid="blog-section" className="bg-navy-deep py-24 lg:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal>
                <div className="label-overline flex items-center gap-3">
                  <span className="h-px w-10 bg-[#C6A87C]" />
                  Insights &amp; Notes
                </div>
              </Reveal>
              <MaskedLinesOnScroll
                className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[#F5F4F0] max-w-2xl"
                lines={[
                  <>Field notes from</>,
                  <>
                    the <em className="italic text-[#C6A87C]">IP trenches.</em>
                  </>,
                ]}
              />
            </div>
            <Reveal delay={0.2}>
              <Link to="/blog" data-testid="blog-section-view-all" className="group inline-flex items-center gap-2 text-[#F5F4F0]/75 hover:text-[#C6A87C] text-xs tracking-[0.14em] uppercase transition-colors duration-300">
                All essays
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>

          {posts.length === 0 ? (
            <div className="text-[#8A99AA] font-light">Loading essays…</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={Math.min(i * 0.08, 0.3)}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`home-blog-card-${p.slug}`}
                    className="group block h-full bg-navy border border-[#C6A87C]/15 overflow-hidden transition-colors duration-500 hover:border-[#C6A87C]/50"
                  >
                    <div className="aspect-[16/10] overflow-hidden clip-frame-sm">
                      <img
                        src={p.cover}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-7">
                      <div className="flex items-center gap-3 text-[11px] text-[#8A99AA] uppercase tracking-[0.18em]">
                        <span>{new Date(p.published_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                        <span className="w-1 h-1 bg-[#C6A87C] rounded-full" />
                        <span>{p.read_time}</span>
                      </div>
                      <h3 className="mt-4 font-serif text-2xl text-[#F5F4F0] leading-tight group-hover:text-[#C6A87C] transition-colors duration-300">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-[#8A99AA] text-sm font-light leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 inline-flex items-center gap-2 text-[#C6A87C] text-xs uppercase tracking-[0.14em]">
                        Read essay <ArrowRight size={13} />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
